import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError, HttpStatusCode } from 'axios';

import { admiTenderService } from './admi-tender.service';
import { GaebUploadTooLargeError, formatMegabytes, prepareGaebUpload, uploadTooLargeProblem } from './gaeb-upload';
import { NotificationSeverity, showSnackbar } from '../notifications/notifications.slice';
import {
    AddSelectionCriterionRequest,
    CreateTenderOptions,
    SelectionCriteriaCatalogItem,
    SelectionCriterionResult,
    Tender,
    TenderSelectionCriterion,
} from './dto/tender.dto';
import { BidderCompanySnapshot } from './dto/offer-submission.dto';
import { RootState } from 'src/store';
import { Company } from '../crm/crm.dto';
import { httpStatusOf, isProblemDetail, isProblemDetailTextOr } from 'src/common/ProblemDetail';
import {
    OfferSubmission,
    OfferSubmissionItem,
    OfferSubmissionItemAssessmentType,
    OfferSubmissionListItem,
} from './dto/offer-submission.dto';
import { AwardCriteria, UpdateAwardCriteriaWeights } from './dto/award-criteria.dto';
import { PriceComparison } from './dto/price-comparison.dto';
import { PaginatedResponseDto } from 'src/infrastructure/admi-client/response.dto';
import { offerProcessingService } from '../offer-processing/offer-processing.service';

export interface State {
    errorMessage: string | null;

    tenderList: PaginatedResponseDto<Tender> | null;

    tender: Tender | null;
    offerSubmissions: OfferSubmissionListItem[];
    selectedOfferSubmission: OfferSubmission | null;
    offerSubmissionsPdfConversionJobs: {
        fileId: string;
        fileName: string;
        status: string;
        extractedJson?: string | null;
    }[];

    // Tender Selection Criteria (displayed criteria for the current tender)
    tenderSelectionCriteria: TenderSelectionCriterion[];
    tenderSelectionCriteriaLoading: boolean;
    tenderSelectionCriteriaError: string | null;

    // Selection Criteria Catalog (available criteria to add)
    selectionCriteriaCatalog: SelectionCriteriaCatalogItem[];
    selectionCriteriaLoading: boolean;
    selectionCriteriaError: string | null;

    priceComparison: PriceComparison | null;

    awardCriteria: AwardCriteria | null;

    selectedBidderCompany: Company | null;
}

const initialState: State = {
    errorMessage: null,

    tenderList: null,

    tender: null,
    offerSubmissions: [],
    selectedOfferSubmission: null,
    offerSubmissionsPdfConversionJobs: [],

    tenderSelectionCriteria: [],
    tenderSelectionCriteriaLoading: false,

    selectedBidderCompany: null,
    tenderSelectionCriteriaError: null,

    selectionCriteriaCatalog: [],
    selectionCriteriaLoading: false,
    selectionCriteriaError: null,

    priceComparison: null,

    awardCriteria: null,
};

export const deleteTender = createAsyncThunk('offerEvaluation/deleteTender', async (tenderId: string, thunkApi) => {
    try {
        await admiTenderService.deleteTender(tenderId);
        return tenderId;
    } catch (err) {
        if (err instanceof AxiosError) {
            return thunkApi.rejectWithValue(err.response?.data ?? err.message);
        }
        return thunkApi.rejectWithValue(err);
    }
});

export const getListOfTenders = createAsyncThunk('offerEvaluation/getListOfTenders', async (page: number, thunkApi) => {
    console.debug('offerEvaluation/getListOfTenders');

    try {
        const response = await admiTenderService.getTenders(page);
        return response;
    } catch (err) {
        if (err instanceof AxiosError) {
            return thunkApi.rejectWithValue(err.response?.data ?? err.message);
        }
        return thunkApi.rejectWithValue(err);
    }
});

// GAEB uploads over the transport limit die at the infrastructure with an opaque 413 the
// browser cannot even read (no CORS headers), so the file is shrunk client-side first and
// any remaining size failure is turned into an actionable message.
function uploadRejectionValue(err: unknown): unknown {
    if (err instanceof GaebUploadTooLargeError) {
        return uploadTooLargeProblem(err.message);
    }
    if (err instanceof AxiosError) {
        if (err.response?.status === HttpStatusCode.PayloadTooLarge && !isProblemDetail(err.response.data)) {
            return uploadTooLargeProblem(
                'The uploaded file exceeds the server upload size limit. ' +
                    'Export the GAEB file without embedded images and try again.',
            );
        }
        return err.response?.data ?? err.message;
    }
    return err;
}

function imagesStrippedMessage(originalFile: File, uploadFile: File): string | null {
    if (uploadFile === originalFile) {
        return null;
    }
    return (
        `Removed ${formatMegabytes(originalFile.size - uploadFile.size)} of embedded images from ` +
        `"${originalFile.name}" to stay under the upload size limit — the bill of quantities data is unaffected.`
    );
}

export const createTender = createAsyncThunk(
    'offerEvaluation/createTender',
    async (options: CreateTenderOptions, thunkApi) => {
        console.debug('offerEvaluation/createTender', options);

        try {
            const costEstimationFile = await prepareGaebUpload(options.costEstimationFile);
            const strippedMessage = imagesStrippedMessage(options.costEstimationFile, costEstimationFile);
            if (strippedMessage) {
                thunkApi.dispatch(showSnackbar({ message: strippedMessage, severity: NotificationSeverity.Info }));
            }

            const response = await admiTenderService.createTender(options.dto, costEstimationFile);
            return response;
        } catch (err) {
            return thunkApi.rejectWithValue(uploadRejectionValue(err));
        }
    },
);

export const hydrateFromQueryParams = createAsyncThunk(
    'offerEvaluation/hydrateFromQueryParams',
    async (tenderId: string | null, thunkApi) => {
        if (!tenderId) {
            return;
        }

        const { tender: currentTender, offerSubmissions: currentOfferSubmissions } = (thunkApi.getState() as RootState)
            .offerEvaluation;
        if (currentTender?.id === tenderId) {
            return { tender: currentTender, offerSubmissions: currentOfferSubmissions };
        }

        try {
            const tender = await admiTenderService.getTender(tenderId);
            const offerSubmissions = await admiTenderService.getOfferSubmissions(tenderId);
            return { tender, offerSubmissions };
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const importOfferSubmission = createAsyncThunk(
    'offerEvaluation/importOfferSubmission',
    async (options: { file: File | null; mimeType: string; offerProcessingFileId?: string }, thunkApi) => {
        console.debug('offerEvaluation/importOfferSubmission', options);

        const tender = (thunkApi.getState() as RootState).offerEvaluation.tender;
        if (!tender) {
            return thunkApi.rejectWithValue(new Error('No tender available for importing offer submissions'));
        }

        try {
            const file = options.file && (await prepareGaebUpload(options.file));
            if (options.file && file) {
                const strippedMessage = imagesStrippedMessage(options.file, file);
                if (strippedMessage) {
                    thunkApi.dispatch(showSnackbar({ message: strippedMessage, severity: NotificationSeverity.Info }));
                }
            }

            const response = await admiTenderService.importOfferSubmission(
                tender.id,
                file,
                options.mimeType,
                options.offerProcessingFileId,
            );
            return response;
        } catch (err) {
            return thunkApi.rejectWithValue(uploadRejectionValue(err));
        }
    },
);

export const triggerOfferSubmissionPdfConversionJob = createAsyncThunk(
    'offerEvaluation/triggerOfferSubmissionPdfConversionJob',
    async (pdfFile: File, thunkApi) => {
        console.debug('offerEvaluation/triggerOfferSubmissionPdfConversionJob');

        try {
            const response = await offerProcessingService.uploadOfferFile(pdfFile);
            return { fileId: response.fileId, fileName: pdfFile.name, status: 'PROCESSING' };
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const checkOfferSubmissionPdfConversionJob = createAsyncThunk(
    'offerEvaluation/checkOfferSubmissionPdfConversionJob',
    async (fileId: string, thunkApi) => {
        console.debug('offerEvaluation/checkOfferSubmissionPdfConversionJob', fileId);

        const jobs = (thunkApi.getState() as RootState).offerEvaluation.offerSubmissionsPdfConversionJobs;
        const job = jobs.find((j) => j.fileId === fileId);

        try {
            const result = await offerProcessingService.getOfferOrNull(fileId);
            if (result?.extractedJson) {
                await thunkApi.dispatch(
                    importOfferSubmission({
                        file: new File([result.extractedJson], job?.fileName ?? fileId),
                        mimeType: 'application/json',
                        offerProcessingFileId: fileId,
                    }),
                );
                return { fileId, done: true };
            }

            return { fileId, done: false };
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const fetchOfferSubmissions = createAsyncThunk(
    'offerEvaluation/getOfferSubmissions',
    async (tenderId: string, thunkApi) => {
        console.debug('offerEvaluation/getOfferSubmissions');

        try {
            const response = await admiTenderService.getOfferSubmissions(tenderId);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const deleteOfferSubmission = createAsyncThunk(
    'offerEvaluation/deleteOfferSubmission',
    async (offerSubmissionId: string, thunkApi) => {
        console.debug('offerEvaluation/deleteOfferSubmission', offerSubmissionId);

        try {
            await admiTenderService.deleteOfferSubmission(offerSubmissionId);
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const selectOfferSubmission = createAsyncThunk(
    'offerEvaluation/selectOfferSubmission',
    async (offerSubmissionId: string | null, thunkApi) => {
        console.debug('offerEvaluation/selectOfferSubmission', offerSubmissionId);

        if (!offerSubmissionId) {
            return null;
        }

        try {
            const response = await admiTenderService.getOfferSubmissionDetails(offerSubmissionId);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const markOfferSubmissionAssessmentError = createAsyncThunk(
    'offerEvaluation/markOfferSubmissionAssessmentError',
    async (
        options: {
            offerSubmissionId: string;
            ordinal: string;
            type: OfferSubmissionItemAssessmentType;
            reason: string;
        },
        thunkApi,
    ) => {
        console.debug('offerEvaluation/markOfferSubmissionAssessmentError', options);

        try {
            if (options.reason === '') {
                await admiTenderService.deleteOfferSubmissionItemAssessmentFailReason(
                    options.offerSubmissionId,
                    options.ordinal,
                    options.type,
                );
            } else {
                await admiTenderService.updateOfferSubmissionItemAssessmentFailReason(
                    options.offerSubmissionId,
                    options.ordinal,
                    options.type,
                    options.reason,
                );
            }
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

/**
 * Fetch Selection Criteria Catalog
 * Retrieves the predefined catalog of selection criteria from the backend.
 */
export const fetchSelectionCriteriaCatalog = createAsyncThunk(
    'offerEvaluation/fetchSelectionCriteriaCatalog',
    async (_, thunkApi) => {
        console.debug('offerEvaluation/fetchSelectionCriteriaCatalog');

        try {
            const response = await admiTenderService.getSelectionCriteriaCatalog();
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

/**
 * Add Selection Criterion to Tender
 * Adds a new selection criterion to the current tender
 */
export const addSelectionCriterion = createAsyncThunk(
    'offerEvaluation/addSelectionCriterion',
    async (request: AddSelectionCriterionRequest, thunkApi) => {
        console.debug('offerEvaluation/addSelectionCriterion', request);

        const tender = (thunkApi.getState() as RootState).offerEvaluation.tender;
        if (!tender) {
            return thunkApi.rejectWithValue(new Error('No tender available for adding selection criterion'));
        }

        try {
            const response = await admiTenderService.addSelectionCriterion(tender.id, request);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

/**
 * Delete Selection Criterion from Tender
 * Removes a selection criterion from the current tender
 */
export const deleteSelectionCriterion = createAsyncThunk(
    'offerEvaluation/deleteSelectionCriterion',
    async (criterionId: string, thunkApi) => {
        console.debug('offerEvaluation/deleteSelectionCriterion', criterionId);

        const tender = (thunkApi.getState() as RootState).offerEvaluation.tender;
        if (!tender) {
            return thunkApi.rejectWithValue(new Error('No tender available for deleting selection criterion'));
        }

        try {
            await admiTenderService.deleteSelectionCriterion(tender.id, criterionId);
            return criterionId;
        } catch (err) {
            // Delete is idempotent: a 404 means the criterion is already gone,
            // which is exactly the outcome the caller wanted (e.g. a double-click
            // where the first request already removed it, or a row left over from
            // another tender). Treat it as success so the optimistic UI removal and
            // the store stay consistent.
            //
            // The status must come from httpStatusOf: the response interceptor
            // rejects with the bare ProblemDetail body, so `err` is not an
            // AxiosError here and `err.response.status` never matched
            // (ADMI-FRONTEND-5Z).
            if (httpStatusOf(err) === HttpStatusCode.NotFound) {
                return criterionId;
            }
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

/**
 * Update Selection Criterion Results
 * Updates the results (offer submission IDs) for a specific selection criterion
 */
export const updateSelectionCriterionResults = createAsyncThunk(
    'offerEvaluation/updateSelectionCriterionResults',
    async ({ criterionId, results }: { criterionId: string; results: SelectionCriterionResult[] }, thunkApi) => {
        console.debug('offerEvaluation/updateSelectionCriterionResults', criterionId, results);

        const tender = (thunkApi.getState() as RootState).offerEvaluation.tender;
        if (!tender) {
            return thunkApi.rejectWithValue(new Error('No tender available for updating selection criterion results'));
        }

        try {
            const response = await admiTenderService.updateSelectionCriterionResults(
                tender.id,
                criterionId,
                results, // Pass array directly
            );
            return { criterionId, results, response };
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const applyChangesOfSelectedOfferSubmission = createAsyncThunk(
    'offerEvaluation/applyChangesOfSelectedOfferSubmission',
    async (_, thunkApi) => {
        console.debug('offerEvaluation/applyChangesOfSelectedOfferSubmission');

        const state = (thunkApi.getState() as RootState).offerEvaluation;
        const offerSubmission = state.selectedOfferSubmission;
        const selectedBidderCompany = state.selectedBidderCompany;

        if (!offerSubmission) {
            return thunkApi.rejectWithValue(new Error('No offer submission available for applying changes'));
        }

        const bidderCompanySnapshot: BidderCompanySnapshot | undefined = selectedBidderCompany
            ? {
                  companyId: selectedBidderCompany.id,
                  companyName: selectedBidderCompany.companyName,
                  domain: selectedBidderCompany.domain,
                  zipCode: selectedBidderCompany.zipCode,
                  city: selectedBidderCompany.city,
                  country: selectedBidderCompany.country,
                  address: selectedBidderCompany.address,
              }
            : undefined;

        try {
            const response = await admiTenderService.updateOfferSubmission(offerSubmission.id, {
                items: offerSubmission.items,
                totalDiscountPercent: offerSubmission.totalDiscountPercent,
                bidderCompanySnapshot,
                // Only send the total price for PDF and manual (those which can be edited)
                totalPrice: ['PDF', 'MANUAL'].includes(offerSubmission.fileFormat)
                    ? offerSubmission.totalPrice
                    : undefined,
            });
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const disqualifySelectedOfferSubmission = createAsyncThunk(
    'offerEvaluation/disqualifySelectedOfferSubmission',
    async (_, thunkApi) => {
        console.debug('offerEvaluation/disqualifySelectedOfferSubmission');

        const offerSubmission = (thunkApi.getState() as RootState).offerEvaluation.selectedOfferSubmission;
        if (!offerSubmission) {
            return thunkApi.rejectWithValue(new Error('No offer submission available for disqualification'));
        }

        try {
            const response = await admiTenderService.updateOfferSubmission(offerSubmission.id, {
                state: 'DISQUALIFIED',
            });
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const fetchPriceComparison = createAsyncThunk(
    'offerEvaluation/fetchPriceComparison',
    async (tenderId: string, thunkApi) => {
        console.debug('offerEvaluation/fetchPriceComparison');

        try {
            const response = await admiTenderService.getPriceComparison(tenderId);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const fetchAwardCriteria = createAsyncThunk(
    'offerEvaluation/fetchAwardCriteria',
    async (tenderId: string, thunkApi) => {
        console.debug('offerEvaluation/fetchAwardCriteria');

        try {
            const response = await admiTenderService.getAwardCriteria(tenderId);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const changeAwardCriteriaWeights = createAsyncThunk(
    'offerEvaluation/updateAwardCriteriaWeights',
    async (weights: UpdateAwardCriteriaWeights, thunkApi) => {
        console.debug('offerEvaluation/updateAwardCriteriaWeights');

        const tender = (thunkApi.getState() as RootState).offerEvaluation.tender;
        if (!tender) {
            return thunkApi.rejectWithValue(new Error('No tender available for updating award criteria weights'));
        }

        try {
            const response = await admiTenderService.changeAwardCriteriaWeights(tender.id, weights);
            return response;
        } catch (err) {
            if (err instanceof AxiosError) {
                return thunkApi.rejectWithValue(err.response?.data ?? err.message);
            }
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const offerEvaluationSlice = createSlice({
    name: 'offerEvaluation',
    initialState,
    reducers: {
        updateOfferSubmissionItem(state, action: PayloadAction<OfferSubmissionItem>) {
            if (!state.selectedOfferSubmission) {
                return;
            }
            state.selectedOfferSubmission.items = state.selectedOfferSubmission.items.map((item) =>
                item.ordinal === action.payload.ordinal ? action.payload : item,
            );
        },
        updateOfferSubmissionTotalDiscount(state, action: PayloadAction<string>) {
            if (!state.selectedOfferSubmission) {
                return;
            }
            state.selectedOfferSubmission.totalDiscountPercent = action.payload;
        },
        updateSelectedBidderCompany(state, action: PayloadAction<Company | null>) {
            state.selectedBidderCompany = action.payload;
        },
        updateOfferSubmissionTotalPrice(state, action: PayloadAction<string>) {
            if (!state.selectedOfferSubmission) {
                return;
            }
            state.selectedOfferSubmission.totalPrice = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getListOfTenders.fulfilled, (state, action) => {
            state.tenderList = action.payload;
        });
        builder.addCase(getListOfTenders.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to get list of tenders');
        });

        builder.addCase(deleteTender.fulfilled, (state, action) => {
            if (state.tenderList) {
                state.tenderList = {
                    ...state.tenderList,
                    content: state.tenderList.content.filter((t) => t.id !== action.payload),
                };
            }
        });
        builder.addCase(deleteTender.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to delete tender');
        });

        builder.addCase(createTender.fulfilled, (state, action) => {
            state.tender = action.payload;
            state.tenderSelectionCriteria = action.payload.selectionCriteria || [];
        });
        builder.addCase(createTender.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to create tender');
        });

        builder.addCase(hydrateFromQueryParams.fulfilled, (state, action) => {
            if (!action.payload) {
                return;
            }

            const { tender, offerSubmissions } = action.payload;
            state.tender = tender;
            state.offerSubmissions = offerSubmissions;
            state.tenderSelectionCriteria = tender.selectionCriteria || [];
        });
        builder.addCase(hydrateFromQueryParams.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to hydrate from query params');
        });

        builder.addCase(fetchOfferSubmissions.fulfilled, (state, action) => {
            state.offerSubmissions = action.payload;
            state.selectedOfferSubmission = null;
        });
        builder.addCase(fetchOfferSubmissions.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to fetch offer submissions');
        });

        builder.addCase(importOfferSubmission.fulfilled, (state, action) => {
            state.offerSubmissions.push(action.payload);
        });
        builder.addCase(importOfferSubmission.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to import offer submission');
        });

        builder.addCase(triggerOfferSubmissionPdfConversionJob.fulfilled, (state, action) => {
            state.offerSubmissionsPdfConversionJobs.push(action.payload);
        });
        builder.addCase(triggerOfferSubmissionPdfConversionJob.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(
                action.payload,
                'Failed to trigger offer submission pdf conversion job',
            );
        });

        builder.addCase(checkOfferSubmissionPdfConversionJob.fulfilled, (state, action) => {
            if (action.payload.done) {
                state.offerSubmissionsPdfConversionJobs = state.offerSubmissionsPdfConversionJobs.filter(
                    (job) => job.fileId !== action.payload.fileId,
                );
            }
        });
        builder.addCase(checkOfferSubmissionPdfConversionJob.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(
                action.payload,
                'Failed to check offer submission pdf conversion job',
            );
        });

        builder.addCase(selectOfferSubmission.fulfilled, (state, action) => {
            state.selectedOfferSubmission = action.payload;

            // Set selectedBidderCompany from bidderCompanySnapshot if it exists
            if (action.payload?.bidderCompanySnapshot) {
                const snapshot = action.payload.bidderCompanySnapshot;
                state.selectedBidderCompany = {
                    id: snapshot.companyId,
                    companyName: snapshot.companyName,
                    zipCode: snapshot.zipCode,
                    domain: snapshot.domain,
                    city: snapshot.city,
                    country: snapshot.country,
                    address: snapshot.address,
                };
            } else {
                state.selectedBidderCompany = null;
            }
        });
        builder.addCase(selectOfferSubmission.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to select offer submission');
        });

        builder.addCase(markOfferSubmissionAssessmentError.fulfilled, (state, action) => {
            if (state.selectedOfferSubmission?.id === action.meta.arg.offerSubmissionId) {
                const item = state.selectedOfferSubmission?.items.find(
                    (item) => item.ordinal === action.meta.arg.ordinal,
                );
                if (item) {
                    const type = action.meta.arg.type;
                    if (action.meta.arg.reason === '') {
                        delete item.assessmentFailReasons[type];
                    } else {
                        item.assessmentFailReasons[type] = action.meta.arg.reason;
                    }
                }
            }

            const offerSubmission = state.priceComparison?.offerSubmissions.find(
                (offerSubmission) => offerSubmission.id === action.meta.arg.offerSubmissionId,
            );
            if (offerSubmission) {
                const offerSubmissionItem = offerSubmission.items.find(
                    (item) => item.ordinal === action.meta.arg.ordinal,
                );
                if (offerSubmissionItem) {
                    const type = action.meta.arg.type;
                    if (action.meta.arg.reason === '') {
                        delete offerSubmissionItem.assessmentFailReasons[type];
                    } else {
                        offerSubmissionItem.assessmentFailReasons[type] = action.meta.arg.reason;
                    }
                }
            }
        });

        // Selection Criteria Catalog
        builder.addCase(fetchSelectionCriteriaCatalog.pending, (state) => {
            state.selectionCriteriaLoading = true;
            state.selectionCriteriaError = null;
        });
        builder.addCase(fetchSelectionCriteriaCatalog.fulfilled, (state, action) => {
            state.selectionCriteriaCatalog = action.payload;
            state.selectionCriteriaLoading = false;
            state.selectionCriteriaError = null;
        });
        builder.addCase(fetchSelectionCriteriaCatalog.rejected, (state, action) => {
            state.selectionCriteriaLoading = false;
            state.selectionCriteriaError = isProblemDetailTextOr(
                action.payload,
                'Failed to load selection criteria catalog',
            );
        });

        // Add Selection Criterion
        builder.addCase(addSelectionCriterion.fulfilled, (state, action) => {
            // The payload is the added/updated criterion from the API
            if (action.payload) {
                const newCriterion = action.payload as TenderSelectionCriterion;

                // Check if a criterion with the same catalogItemId already exists
                const existingIndex = state.tenderSelectionCriteria.findIndex(
                    (c) => c.catalogItemId && c.catalogItemId === newCriterion.catalogItemId,
                );

                if (existingIndex !== -1) {
                    // Replace the existing criterion (in-place update)
                    state.tenderSelectionCriteria[existingIndex] = newCriterion;
                } else {
                    // Add as new criterion
                    state.tenderSelectionCriteria.push(newCriterion);
                }
            }
        });

        // Delete Selection Criterion
        builder.addCase(deleteSelectionCriterion.fulfilled, (state, action) => {
            // Remove the criterion from the list
            state.tenderSelectionCriteria = state.tenderSelectionCriteria.filter((c) => c.id !== action.payload);
        });

        // Update Selection Criterion Results
        builder.addCase(updateSelectionCriterionResults.fulfilled, (state, action) => {
            // Update the results for the specific criterion
            const criterion = state.tenderSelectionCriteria.find((c) => c.id === action.payload.criterionId);
            if (criterion) {
                criterion.results = action.payload.results;
            }
        });
        builder.addCase(markOfferSubmissionAssessmentError.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(
                action.payload,
                'Failed to mark offer submission assessment error',
            );
        });

        builder.addCase(applyChangesOfSelectedOfferSubmission.fulfilled, (state, action) => {
            state.selectedOfferSubmission = action.payload;
            state.offerSubmissions = state.offerSubmissions.map((offerSubmission) =>
                offerSubmission.id === action.payload.id ? action.payload : offerSubmission,
            );
        });
        builder.addCase(applyChangesOfSelectedOfferSubmission.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to apply changes. Check the inputs.');
        });

        builder.addCase(disqualifySelectedOfferSubmission.fulfilled, (state, action) => {
            state.selectedOfferSubmission = action.payload;
            state.offerSubmissions = state.offerSubmissions.map((offerSubmission) =>
                offerSubmission.id === action.payload.id ? action.payload : offerSubmission,
            );
        });
        builder.addCase(disqualifySelectedOfferSubmission.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to disqualify offer submission');
        });

        builder.addCase(fetchPriceComparison.fulfilled, (state, action) => {
            state.priceComparison = action.payload;
        });
        builder.addCase(fetchPriceComparison.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to fetch price comparison');
        });

        builder.addCase(fetchAwardCriteria.fulfilled, (state, action) => {
            state.awardCriteria = action.payload;
        });
        builder.addCase(fetchAwardCriteria.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to fetch award criteria');
        });

        builder.addCase(changeAwardCriteriaWeights.fulfilled, (state, action) => {
            state.awardCriteria = action.payload;
        });
        builder.addCase(changeAwardCriteriaWeights.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed to update award criteria weights');
        });
    },
});

export const {
    updateOfferSubmissionItem,
    updateOfferSubmissionTotalDiscount,
    updateSelectedBidderCompany,
    updateOfferSubmissionTotalPrice,
} = offerEvaluationSlice.actions;

export default offerEvaluationSlice.reducer;
